import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { visaApi, VISA_STATUS_BY_ID, VISA_STATUSES } from "@/lib/visa";
import { fmtDate, fmtDateTime, relativeTime, countryFlag } from "@/lib/format";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Download, Mail, Upload, RefreshCw, Clock, Users, Plane, Stethoscope, User, ExternalLink } from "lucide-react";

export default function VisaRequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [req, setReq] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [docs, setDocs] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [changingStatus, setChangingStatus] = useState(false);
  const [uploadPct, setUploadPct] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [r, t, d, v] = await Promise.all([
        visaApi.getRequest(id),
        visaApi.timeline(id),
        visaApi.listDocs(id),
        visaApi.letterVersions(id),
      ]);
      setReq(r); setTimeline(t); setDocs(d); setVersions(v);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-[#5C6660]">Loading…</div>;
  if (!req) return <div className="text-sm text-[#5C6660]">Not found</div>;

  const p = req.patient_info || {};
  const m = req.medical_info || {};
  const t = req.travel_details || {};
  const st = VISA_STATUS_BY_ID[req.status] || VISA_STATUS_BY_ID.draft;
  const canEdit = user?.role !== "patient" || req.status === "draft" || req.status === "documents_requested";
  const canGenerate = ["admin", "coordinator"].includes(user?.role);

  const changeStatus = async (newStatus) => {
    setChangingStatus(true);
    try {
      await visaApi.changeStatus(id, newStatus);
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally { setChangingStatus(false); }
  };

  const generate = async () => {
    try {
      await visaApi.generateLetter(id);
      toast.success("Letter generated");
      load();
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const emailLetter = async (versionId) => {
    try {
      const r = await visaApi.emailLetter(id, versionId);
      toast.success(`Letter emailed to ${r.sent_to}`);
      load();
    } catch (e) { toast.error(getApiErrorMessage(e)); }
  };

  const uploadFile = async (file, category = "other", companionId = null) => {
    setUploadPct(0);
    try {
      const useChunked = file.size > 5 * 1024 * 1024;
      if (useChunked) {
        await visaApi.chunkedUpload(id, file, category, companionId, setUploadPct);
      } else {
        await visaApi.uploadDoc(id, file, category, companionId, setUploadPct);
      }
      toast.success("Uploaded");
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally { setUploadPct(null); }
  };

  return (
    <div data-testid="visa-detail-page">
      <PageHeader
        eyebrow={`Reference · ${req.reference_number || "Pending assignment"}`}
        title={p.full_name || "Visa Request"}
        subtitle={`${countryFlag(p.nationality)} ${p.nationality || "Unknown"} · Passport ${p.passport_number || "—"}`}
        actions={
          <div className="flex items-center gap-2">
            <span style={{ background: st.bg, color: st.color }} className="px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider" data-testid="visa-current-status">
              {st.label}
            </span>
          </div>
        }
      />

      {/* Status transition strip */}
      {canEdit && user?.role !== "patient" && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-4 mb-5">
          <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-2">Move to next status</div>
          <div className="flex flex-wrap gap-2" data-testid="status-transitions">
            {VISA_STATUSES.map((s) => (
              <button key={s.id} disabled={s.id === req.status || changingStatus}
                onClick={() => changeStatus(s.id)}
                data-testid={`transition-${s.id}`}
                style={{ borderColor: s.color, color: s.id === req.status ? "#fff" : s.color, background: s.id === req.status ? s.color : "transparent" }}
                className="border rounded-full px-3 py-1 text-[11px] font-semibold hover:opacity-80 disabled:opacity-40">
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-white border border-[#E8E6E1] rounded-full p-1 mb-5 w-fit">
        {[["overview", "Overview"], ["letter", "Invitation Letter"], ["documents", "Documents"], ["timeline", "Timeline"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-xs font-medium transition ${tab === k ? "bg-[#2A4D3E] text-white" : "text-[#5C6660] hover:bg-[#F7F5F2]"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <InfoCard icon={User} title="Patient Information" data={[
            ["Full name", p.full_name], ["Gender", p.gender], ["Date of birth", fmtDate(p.date_of_birth)],
            ["Nationality", p.nationality], ["Passport", p.passport_number],
            ["Passport expiry", fmtDate(p.passport_expiry)],
            ["Country of residence", p.country_of_residence],
            ["Mobile", p.mobile_number], ["Email", p.email],
          ]} />
          <InfoCard icon={Stethoscope} title="Medical" data={[
            ["Diagnosis", m.diagnosis], ["Department", m.department],
            ["Hospital", m.hospital_name], ["Doctor", m.doctor_name],
            ["Procedure", m.planned_procedure],
            ["Admission date", fmtDate(m.expected_admission_date)],
            ["Estimated stay", m.estimated_stay],
          ]} />
          <InfoCard icon={Plane} title="Travel" data={[
            ["Arrival", fmtDate(t.proposed_arrival_date)],
            ["Departure", fmtDate(t.proposed_departure_date)],
            ["Arrival airport", t.arrival_airport], ["Departure airport", t.departure_airport],
            ["Preferred city", t.preferred_city],
            ["Accommodation", t.accommodation_required ? "Yes" : "No"],
            ["Airport pickup", t.airport_pickup_required ? "Yes" : "No"],
          ]} />
          <div className="lg:col-span-3 bg-white border border-[#E8E6E1] rounded-2xl p-5">
            <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-3 flex items-center gap-2"><Users size={14} /> Companions ({req.companions?.length || 0})</div>
            {(req.companions || []).length === 0 ? <div className="text-sm text-[#8E9992]">None</div> : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {req.companions.map((c) => (
                  <div key={c.id} className="border border-[#E8E6E1] rounded-xl p-3">
                    <div className="text-sm font-medium text-[#2A332C]">{c.full_name}</div>
                    <div className="text-xs text-[#8E9992]">{c.relationship} · {c.nationality}</div>
                    <div className="text-[11px] text-[#5C6660] mt-1 font-mono">{c.passport_number}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invitation Letter */}
      {tab === "letter" && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-sm font-semibold text-[#2A332C]">Invitation Letter</div>
              <div className="text-xs text-[#8E9992]">Reference: {req.reference_number || "Not assigned yet — moves to draft on generation"}</div>
            </div>
            <div className="flex gap-2">
              <a href={visaApi.previewLetterUrl(id)} target="_blank" rel="noreferrer"
                className="border border-[#2A4D3E] text-[#2A4D3E] rounded-full px-4 py-1.5 text-xs font-medium hover:bg-[#E6F0EB] flex items-center gap-1.5" data-testid="preview-letter-btn">
                <ExternalLink size={12} /> Preview
              </a>
              {canGenerate && (
                <button onClick={generate} className="bg-[#2A4D3E] text-white rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1.5" data-testid="generate-letter-btn">
                  <RefreshCw size={12} /> Generate / Regenerate
                </button>
              )}
            </div>
          </div>

          {versions.length === 0 ? (
            <div className="text-sm text-[#8E9992] bg-[#FAFAF7] rounded-xl p-8 text-center">
              No letter generated yet. Preview auto-populates from patient data — click &quot;Generate&quot; to save as version.
            </div>
          ) : (
            <div className="space-y-2">
              {versions.slice().reverse().map((v) => (
                <div key={v.id} className="flex items-center gap-4 border border-[#E8E6E1] rounded-xl p-3" data-testid={`letter-version-${v.version}`}>
                  <FileText size={20} className="text-[#2A4D3E]" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2A332C]">Version {v.version}</div>
                    <div className="text-[11px] text-[#8E9992]">
                      {fmtDateTime(v.generated_at)} · {v.template_name} · {Math.round(v.size / 1024)} KB
                    </div>
                  </div>
                  <a href={visaApi.letterDownloadUrl(id, v.id)} target="_blank" rel="noreferrer"
                    className="border border-[#E8E6E1] rounded-full px-3 py-1.5 text-xs font-medium text-[#5C6660] hover:bg-[#FAFAF7] flex items-center gap-1.5" data-testid={`download-v${v.version}`}>
                    <Download size={12} /> Download
                  </a>
                  {canGenerate && (
                    <button onClick={() => emailLetter(v.id)}
                      className="bg-[#D97757] text-white rounded-full px-3 py-1.5 text-xs font-medium flex items-center gap-1.5" data-testid={`email-v${v.version}`}>
                      <Mail size={12} /> Email to patient
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Documents */}
      {tab === "documents" && (
        <div>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl p-4 mb-4 flex items-center justify-between">
            <div className="text-xs text-[#8E9992]">PDF, JPG, PNG · Max 25 MB · Chunked upload for files &gt; 5 MB</div>
            <label className="cursor-pointer">
              <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0], "other")}
                data-testid="detail-upload-input" />
              <div className="bg-[#2A4D3E] text-white rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1.5">
                <Upload size={12} /> {uploadPct != null ? `Uploading ${uploadPct}%` : "Upload document"}
              </div>
            </label>
          </div>
          {docs.length === 0 ? (
            <div className="bg-white border border-[#E8E6E1] rounded-2xl p-10 text-center text-sm text-[#8E9992]">No documents uploaded.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {docs.map((d) => (
                <div key={d.id} className="bg-white border border-[#E8E6E1] rounded-xl p-4 flex items-center gap-3" data-testid={`doc-${d.id}`}>
                  <FileText size={22} className="text-[#2A4D3E] flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2A332C] truncate">{d.name}</div>
                    <div className="text-[11px] text-[#8E9992]">{d.category} · {Math.round(d.size / 1024)} KB · {relativeTime(d.uploaded_at)}</div>
                  </div>
                  <a href={visaApi.downloadDoc(d.id)} target="_blank" rel="noreferrer" className="text-[#2A4D3E] hover:opacity-70">
                    <Download size={14} />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      {tab === "timeline" && (
        <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5" data-testid="visa-timeline">
          {timeline.length === 0 ? (
            <div className="text-sm text-[#8E9992] text-center py-6">No events yet.</div>
          ) : (
            <ol className="relative border-l border-[#E8E6E1] ml-3 space-y-4">
              {timeline.map((e) => (
                <li key={e.id} className="ml-6">
                  <span className="absolute -left-1.5 flex items-center justify-center w-3 h-3 bg-[#2A4D3E] rounded-full"></span>
                  <div className="text-sm font-medium text-[#2A332C]">{e.title}</div>
                  {e.description && <div className="text-xs text-[#5C6660] mt-0.5">{e.description}</div>}
                  <div className="text-[11px] text-[#8E9992] mt-1">
                    <Clock size={10} className="inline mr-1" />{fmtDateTime(e.created_at)} · {e.actor_name || "system"}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon: Icon, title, data }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-2xl p-5">
      <div className="text-xs uppercase tracking-wider text-[#8E9992] font-semibold mb-3 flex items-center gap-2">
        <Icon size={14} /> {title}
      </div>
      <dl className="space-y-2">
        {data.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-4">
            <dt className="text-xs text-[#8E9992]">{k}</dt>
            <dd className="text-sm text-[#2A332C] text-right break-words">{v || "—"}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
